"use client";

import * as React from "react";
import {
  AdminPortalOpenButton,
  AdminPortalSsoConnectionError,
  AdminPortalSsoConnectionLoading,
  AdminPortalSsoConnection as AdminPortalSsoConnectionPresentational,
} from "./lib/admin-portal-sso-connection.js";
import type {
  AdminPortalSsoConnectionErrorProps,
  AdminPortalSsoConnectionLoadingProps,
  AdminPortalSsoConnectionStatusProps,
} from "./lib/admin-portal-sso-connection.js";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import {
  type SsoConnection,
  useListSsoConnections,
  useGenerateAdminPortalLink,
} from "./api/endpoint.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import {
  openAdminPortalViaForm,
  unreachable,
  WidgetRootDomProps,
} from "./lib/utils.js";
import { IdentityProvider } from "./lib/identity-providers.js";

interface AdminPortalSsoConnectionProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const AdminPortalSsoConnection: React.FC<AdminPortalSsoConnectionProps> = ({
  authToken,
  ...domProps
}) => {
  const baseUrl = useWorkOsApiUrl();
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <AdminPortalSsoConnectionError error={error} {...domProps} />
      )}
    >
      <ApiProvider
        widgetType="admin-portal-sso-connection"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <AdminPortalSsoConnectionImpl {...domProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

const AdminPortalSsoConnectionImpl = (props: WidgetRootDomProps) => {
  const isApiReady = useApiReady();
  const [currentDate, setCurrentDate] = React.useState<Date | null>(() =>
    isApiReady ? new Date() : null,
  );
  if (isApiReady && currentDate === null) {
    setCurrentDate(new Date());
  }

  const { mutate: generateAdminPortalLink, isPending } =
    useGenerateAdminPortalLink({
      mutation: {
        onSuccess: (data) => {
          openAdminPortalViaForm(data.link, {
            target: "_blank",
            rel: "noopener noreferrer",
          });
        },
      },
    });

  const query = useListSsoConnections();

  const initConfig = () => {
    generateAdminPortalLink({
      params: {
        intent: "sso",
      },
    });
  };

  if (query.isLoading) {
    return <AdminPortalSsoConnectionLoading {...props} />;
  }

  if (query.isError) {
    return <AdminPortalSsoConnectionError error={query.error} {...props} />;
  }

  if (query.data) {
    const connection = query.data[0];
    if (!connection) {
      return (
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <AdminPortalSsoConnectionError error={error} {...props} />
          )}
        >
          <AdminPortalSsoConnectionPresentational
            {...props}
            connectionStatus="NotConfigured"
            currentDate={currentDate}
            lastSession={null}
            adminPortalOpenButton={
              <AdminPortalOpenButton
                isPending={isPending}
                initConfig={initConfig}
              />
            }
          />
        </ErrorBoundary>
      );
    }

    const connectionState = getConnectionState(connection, currentDate);
    const statusProps: AdminPortalSsoConnectionStatusProps = (() => {
      const connectionStatus = connectionState.status;
      if (!connection || connectionStatus === "NotConfigured") {
        return {
          connectionStatus: "NotConfigured",
        } satisfies AdminPortalSsoConnectionStatusProps;
      }

      const identityProvider = getIdentityProvider(connection);
      switch (connectionStatus) {
        case "Active":
          return {
            connectionStatus,
            identityProvider,
          } satisfies AdminPortalSsoConnectionStatusProps;
        case "Inactive":
          return {
            connectionStatus,
            identityProvider,
          } satisfies AdminPortalSsoConnectionStatusProps;
        case "Expired":
        case "Expiring":
          return {
            connectionStatus,
            expiryDate: connectionState.expiryDate,
            identityProvider,
          } satisfies AdminPortalSsoConnectionStatusProps;
        default:
          return unreachable(connectionState);
      }
    })();

    const lastSession = connection.lastSession
      ? new Date(connection.lastSession.createdAt)
      : null;

    return (
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <AdminPortalSsoConnectionError error={error} {...props} />
        )}
      >
        <AdminPortalSsoConnectionPresentational
          {...props}
          {...statusProps}
          currentDate={currentDate}
          lastSession={lastSession}
          adminPortalOpenButton={
            <AdminPortalOpenButton
              isPending={isPending}
              initConfig={initConfig}
            />
          }
        />
      </ErrorBoundary>
    );
  }
};

type ConnectionState =
  | { status: "NotConfigured" }
  | { status: "Active" }
  | { status: "Inactive" }
  | {
      status: "Expiring";
      daysUntilExpiry: number | null;
      expiryDate: Date | null;
    }
  | { status: "Expired"; expiryDate: Date | null };

function getConnectionState(
  connection: SsoConnection,
  currentDate: Date | null,
): ConnectionState {
  switch (connection.state) {
    case "Active":
    case "Validating":
    case "Deleting": {
      const activeStatus = { status: "Active" } satisfies ConnectionState;
      if (connection.providerTag !== "Saml") {
        return activeStatus;
      }

      if (connection.latestExpiredCertificate) {
        return {
          status: "Expired",
          expiryDate: connection.latestExpiredCertificate.notAfter
            ? new Date(connection.latestExpiredCertificate.notAfter)
            : null,
        };
      }

      const expiryTime = connection.latestExpiringCertificate?.notAfter;
      if (currentDate == null || expiryTime == null) {
        return activeStatus;
      }

      const expiryDate = new Date(expiryTime);
      const daysUntilExpiry = (() => {
        const diff = expiryDate.getTime() - currentDate.getTime();
        if (diff < 0) {
          return -1;
        }

        return diff / (1000 * 60 * 60 * 24);
      })();

      if (daysUntilExpiry !== -1 && daysUntilExpiry <= 30) {
        return { status: "Expiring", daysUntilExpiry, expiryDate };
      }

      return activeStatus;
    }
    case "Inactive": {
      return { status: "Inactive" };
    }
    default:
      return unreachable(connection.state);
  }
}

function getIdentityProvider(connection: SsoConnection): IdentityProvider {
  switch (connection.type) {
    case "AdfsSaml":
    case "EntraIdOidc":
      return "microsoft";
    case "Auth0Migration":
    case "Auth0Saml":
      return "auth0";
    case "AzureSaml":
      return "azure";
    case "CasSaml":
      return "cas";
    case "ClassLinkSaml":
      return "classlink";
    case "CloudflareSaml":
      return "cloudflare";
    case "CyberArkSaml":
      return "cyberark";
    case "DuoSaml":
      return "duo";
    case "GenericSaml":
      return "generic-saml";
    case "GoogleSaml":
    case "GoogleOidc":
      return "google";
    case "JumpCloudSaml":
      return "jumpcloud";
    case "KeycloakSaml":
      return "keycloak";
    case "LastPassSaml":
      return "lastpass";
    case "MiniOrangeSaml":
      return "miniorange";
    case "NetIqSaml":
      return "net-iq";
    case "OktaOidc":
    case "OktaSaml":
      return "okta";
    case "OneLoginSaml":
      return "onelogin";
    case "OracleSaml":
      return "oracle";
    case "PingFederateSaml":
    case "PingOneSaml":
      return "ping-identity";
    case "RipplingSaml":
      return "rippling";
    case "SalesforceSaml":
      return "salesforce";
    case "ShibbolethGenericSaml":
    case "ShibbolethSaml":
      return "shibboleth";
    case "SimpleSamlPhpSaml":
      return "simple-saml-php";
    case "TestIdp":
      return "test-idp";
    case "VmWareSaml":
      return "vmware";
    case "AdpOidc":
      return "adp";
    case "GenericOidc":
      return "generic-oidc";
    case "LoginGovOidc":
      return "login-gov";
    case "CleverOidc":
      return "clever";
    default:
      return unreachable(connection);
  }
}

export type {
  AdminPortalSsoConnectionProps,
  AdminPortalSsoConnectionLoadingProps,
  AdminPortalSsoConnectionErrorProps,
};
export {
  AdminPortalSsoConnection,
  AdminPortalSsoConnectionLoading,
  AdminPortalSsoConnectionError,
};
